export async function fileToImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });

    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
export async function scaleBlob(
  src: Blob,
  maxW: number,
  maxH: number,
  mime: string = 'image/png',
  quality?: number
): Promise<Blob> {
  let bitmap: ImageBitmap | null = null;
  let img: HTMLImageElement | null = null;
  const url = URL.createObjectURL(src);

  try {
    // ------------------------------------------------------------
    // 1️⃣ Decode source
    // ------------------------------------------------------------
    if ('createImageBitmap' in window) {
      try {
        bitmap = await createImageBitmap(src);
      } catch {
        bitmap = null;
      }
    }

    let sw: number;
    let sh: number;

    if (bitmap) {
      sw = bitmap.width;
      sh = bitmap.height;
    } else {
      img = new Image();
      img.decoding = 'async';
      img.src = url;

      await new Promise<void>((res, rej) => {
        img!.onload = () => res();
        img!.onerror = () => rej(new Error('Image load failed'));
      });

      sw = img.naturalWidth || img.width;
      sh = img.naturalHeight || img.height;
    }

    // ------------------------------------------------------------
    // 2️⃣ Compute target size
    // ------------------------------------------------------------
    if (sw <= maxW && sh <= maxH) {
      return src;
    }

    const scale = Math.min(maxW / sw, maxH / sh);
    const tw = Math.max(1, Math.round(sw * scale));
    const th = Math.max(1, Math.round(sh * scale));

    // ------------------------------------------------------------
    // 3️⃣ Fast path: ImageBitmap resize (best for large images)
    // ------------------------------------------------------------
    if ('createImageBitmap' in window && bitmap) {
      try {
        const resized = await createImageBitmap(bitmap, {
          resizeWidth: tw,
          resizeHeight: th,
          resizeQuality: 'high',
        });

        const canvas = document.createElement('canvas');
        canvas.width = tw;
        canvas.height = th;

        const ctx = canvas.getContext('2d');
        if (!ctx) { throw new Error('2D context unavailable'); }

        ctx.drawImage(resized, 0, 0);

        const out = await canvasToBlob(canvas, mime, quality);

        resized.close();
        return out;
      } catch {
        // fall through to canvas downscale
      }
    }

    // ------------------------------------------------------------
    // 4️⃣ Safe progressive canvas downscale (ping-pong)
    // ------------------------------------------------------------
    const canvasA = document.createElement('canvas');
    const ctxA = canvasA.getContext('2d')!;
    const canvasB = document.createElement('canvas');
    const ctxB = canvasB.getContext('2d')!;

    let source: CanvasImageSource = bitmap ?? img!;
    let cw = sw;
    let ch = sh;

    let targetCanvas = canvasA;
    let targetCtx = ctxA;

    while (cw * 0.5 > tw && ch * 0.5 > th) {
      cw = Math.round(cw * 0.5);
      ch = Math.round(ch * 0.5);

      targetCanvas.width = cw;
      targetCanvas.height = ch;
      targetCtx.clearRect(0, 0, cw, ch);
      targetCtx.drawImage(source, 0, 0, cw, ch);

      source = targetCanvas;
      targetCanvas = targetCanvas === canvasA ? canvasB : canvasA;
      targetCtx = targetCtx === ctxA ? ctxB : ctxA;
    }

    // Final draw
    targetCanvas.width = tw;
    targetCanvas.height = th;
    targetCtx.clearRect(0, 0, tw, th);
    targetCtx.drawImage(source, 0, 0, tw, th);

    return await canvasToBlob(targetCanvas, mime, quality);
  } finally {
    // ------------------------------------------------------------
    // 5️⃣ Cleanup (critical for iOS / GPU memory)
    // ------------------------------------------------------------
    if (bitmap) { bitmap.close(); }
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('canvas.toBlob failed'))),
      mime,
      quality
    );
  });
}

export function dataURItoBlob(dataURI: string): Blob {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], {type: mimeString});
}



export type ManagedImage = { image: HTMLImageElement; dispose: () => void };

export async function fileToManagedImage(file: File): Promise<ManagedImage> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });

  return {
    image: img,
    dispose: () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      URL.revokeObjectURL(url);
    },
  };
}
export async function downscaleFileIfNeeded(file: File, maxSide = 2000): Promise<File> {
  const blob = await scaleBlob(file, maxSide, maxSide, 'image/png', 0.85);

  if (blob === file) {
    return file;
  }

  return new File([blob], file.name, {
    type: 'image/png',
    lastModified: Date.now(),
  });
}
